import { ajvResolver } from "@hookform/resolvers/ajv";
import { Form as FormRemix, Link, useNavigation } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { Controller, useController, useForm } from "react-hook-form";
import { TFormRegister } from "~/routes/register";
import { fullFormats } from "ajv-formats/dist/formats";
import {
  countCharacters,
  defaultDigitCharacters,
  defaultLowerCharacters,
  defaultSpecialCharacters,
  defaultUpperCharacters,
  strongPasswordFormat,
} from "~/lib/ajv/strongPassword";
import { TFormLogin } from "~/routes/login";
import { CardText } from "../components/CardText";
import { Heading } from "./Heading";
import { Paragraph } from "./Paragraph";
import { Button } from "./Button";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  InfoCircle,
  Plus,
  RefreshCcw02,
  X,
} from "@untitled-ui/icons-react";
import * as styles from "./Form.css";
import { CardTextRadio } from "../components/CardTextRadio";
import { ButtonIcon } from "./ButtonIcon";
import { MediaBackground } from "../components/MediaBackground";
import { JourniWordMark } from "../../assets/svgs/JourniWordMark";
import { FormInputFieldWrapper } from "../components/FormInputFieldWrapper";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import Icon from "./Icon";
import DrawnBorder from "~/assets/svgs/DrawnBorder";
import { WrapperDrawnBorder } from "../wrappers/WrapperDrawnBorder";
import { TFormClassCreate } from "~/routes/dashboard_/classes/create/route";
import { TFormSessionCreate } from "~/routes/dashboard_/classes/$classId_/modules/$moduleId/sessions/create/route";
import { ModalAlert } from "../modals/ModalAlert";
import { ModalForm } from "../modals/ModalForm";
import { WrapperFormRoot } from "../wrappers/WrapperFormRoot";
import { FormConfirmation } from "../components/FormConfirmation";
import { TFormReset } from "~/routes/reset";
import { TFormRecover } from "~/routes/recover";
import { TFormAccountEdit } from "~/routes/dashboard_/account/edit/route";
import { TFormClassEdit } from "~/routes/dashboard_/classes/$classId_/edit/route";
import {
  Turnstile,
  TurnstileInstance,
  TurnstileProps,
} from "@marsidev/react-turnstile";
import { CardTurnstile } from "../components/CardTurnstile";
import { TFormAccountSecurity } from "~/routes/dashboard_/account/security/route";
import { WALKTHROUGH } from "~/lib/constants";
import { SectionWalkthrough } from "../sections/SectionWalkthrough";
import { TFormJoin } from "~/routes/join";

interface FormProps {
  form:
    | TFormRegister
    | TFormLogin
    | TFormClassCreate
    | TFormSessionCreate
    | TFormReset
    | TFormRecover
    | TFormAccountEdit
    | TFormAccountSecurity
    | TFormClassEdit
    | TFormJoin;
  responseError?: string;
  responseSuccess?: string;
  responseWarning?: {
    title: string;
    text: string;
    actions: {
      name: string;
      value?: string;
      label: string;
    }[];
  };
  responseTimestamp?: number;
  turnstile?: TurnstileProps;
  walkthrough?: boolean;
}

export const Form = ({
  form,
  responseError,
  responseSuccess,
  responseWarning,
  responseTimestamp,
  turnstile,
  walkthrough = false,
}: FormProps) => {
  const [step, setStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [turnstileVerification, setTurnstileVerification] = useState<
    "error" | "expired" | "solved" | null
  >(null);
  const isStepLast = step === form.inputs.length - 1;

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    getValues,
    control,
  } = useForm({
    resolver: ajvResolver(form.schema[step] as any, {
      formats: {
        ...fullFormats,
        ...strongPasswordFormat({
          passwordCriteria: {
            minUpper: 1,
            minLower: 1,
            minDigit: 1,
            minSpecial: 1,
            minLength: 8,
          },
        }),
      },
      $data: true,
    }),
    mode: "onChange",
  });

  const getTurnstileChallanageStates = (
    turnstileVerification: "error" | "expired" | "solved" | null,
  ) => {
    if (turnstileVerification === "error") {
      return {
        label: "Verification failed, please refresh and try again",
        icon: X,
        theme: "negative",
        rotateIcon: false,
      };
    } else if (turnstileVerification === "expired") {
      return {
        label: "Verification failed, please refresh and try again",
        icon: X,
        theme: "negative",
        rotateIcon: false,
      };
    } else if (turnstileVerification === "solved") {
      return {
        label: "Verified",
        icon: Check,
        theme: "positive",
        rotateIcon: false,
      };
    } else if (turnstileVerification === null) {
      return {
        label: "Verifying",
        icon: RefreshCcw02,
        theme: "primary",
        rotateIcon: true,
      };
    }
  };

  const turnstileChallengeState = getTurnstileChallanageStates(
    turnstileVerification,
  );

  const refTurnstile = useRef<TurnstileInstance>(null);

  useEffect(() => {
    if (responseError) {
      setTurnstileVerification(null);
      refTurnstile.current?.reset();
    }
  }, [responseError, responseTimestamp]);

  const onSubmit = handleSubmit((data) => {
    if (!isStepLast) {
      setStep(step + 1);
    } else {
      try {
        return formSubmit(data).then((response) => {
          if (response.ok) {
            setSuccess(true);
          } else {
            setError(true);
          }
        });
      } catch (error) {
        setError(true);
      }
    }
  });

  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const [walkthroughOpen, setWalkthroughOpen] = useState(true);

  // console.log("errors", errors);
  // console.log("isValid", isValid);
  // console.log("getValues", getValues());

  return (
    <section
      style={{
        display: "grid",
        height: "100dvh",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr auto",
        overflow: "hidden",
        justifyContent: "center",
      }}
    >
      <WrapperFormRoot>
        {walkthrough && (
          <SectionWalkthrough
            steps={WALKTHROUGH}
            defaultOpen={true}
            open={walkthroughOpen}
            setOpen={setWalkthroughOpen}
          />
        )}
        <div className={styles.formWrapper}>
          <MediaBackground
            media={{
              image: form.background.image,
            }}
          />
          <div className={styles.navBar}>
            <Link to={"https://journi.au/"}>
              <JourniWordMark className={styles.wordMark} />
            </Link>
          </div>
          <FormRemix
            method="post"
            onSubmit={!isValid || !isStepLast ? onSubmit : () => {}}
            className={styles.formContainer}
            noValidate
          >
            <div
              className={styles.form}
              style={{ opacity: isModalOpen ? 0 : 1 }}
            >
              <div className={styles.titleContainer}>
                <CardText width="full">
                  <Heading as="h1" size="XxLarge">
                    {form.title}
                  </Heading>
                </CardText>
                {form.subtitle && (
                  <Paragraph size="Sm">
                    {form.subtitle.text}
                    {form.subtitle.link && (
                      <>
                        <span> </span>
                        <Link
                          to={form.subtitle.link.to}
                          className={styles.link}
                        >
                          {form.subtitle.link.text}
                        </Link>
                      </>
                    )}
                  </Paragraph>
                )}
              </div>
              {form.inputs.map((inputGroup, index) => {
                return (
                  <div
                    style={{
                      display: index === step ? "flex" : "none",
                    }}
                    className={styles.inputGroup}
                    key={index}
                  >
                    {inputGroup.map((input, index) => {
                      const [query, setQuery] = useState("");
                      const [selected, setSelected] = useState<
                        string | number
                      >();

                      // const filteredOptions =
                      //   query === ""
                      //     ? "options" in input && input.options
                      //     : "options" in input &&
                      //       input.options != undefined &&
                      //       input.options.filter((option) => {
                      //         return option.label
                      //           ?.toString()
                      //           .toLowerCase()
                      //           .includes(query.toLowerCase());
                      //       });

                      const filteredOptions =
                        query === ""
                          ? "options" in input
                            ? input.options
                            : []
                          : "options" in input
                            ? input.options != undefined &&
                              input.options.filter((option) => {
                                return option.label
                                  ?.toString()
                                  .toLowerCase()
                                  .includes(query.toLowerCase());
                              })
                            : [];

                      const { field } = useController({
                        control,
                        name: `${input.name}`,

                        // Default value required to make component controlled (should aways recieve a string value, never null or undefined)
                        defaultValue:
                          "value" in input && input.value
                            ? input.value.toString()
                            : "",
                      });

                      const ComboboxComponent = (
                        <div
                          style={{
                            zIndex: inputGroup.length - index,
                          }}
                          className={styles.inputCombobox}
                        >
                          <FormInputFieldWrapper
                            selected={
                              Boolean(getValues(input.name)) ||
                              Boolean(responseError) ||
                              Boolean(errors[input.name])
                            }
                            theme={
                              Boolean(errors[input.name]) ||
                              Boolean(responseError)
                                ? "negative"
                                : "positive"
                            }
                          >
                            {/* Real field that Combobox field maps to needs to be a hidden field */}
                            <input type="hidden" {...field} />
                            <Combobox
                              virtual={{ options: filteredOptions }}
                              value={field.value}
                              onChange={(option) => {
                                const selectedOption =
                                  typeof option === "object" &&
                                  option &&
                                  option !== null &&
                                  option.value
                                    ? option.value.toString()
                                    : option !== null && option.toString();

                                // console.log("option", option);
                                // console.log("selectedOption", selectedOption);
                                selectedOption &&
                                  (field.onChange(selectedOption),
                                  setSelected(selectedOption));
                              }}
                              onClose={() => setQuery("")}
                            >
                              <div className={styles.formComboboxInput}>
                                <ComboboxInput
                                  className={styles.formCombobox}
                                  readOnly={
                                    "options" in input &&
                                    Array.isArray(input.options) &&
                                    input.options.length <= 5
                                  }
                                  displayValue={() =>
                                    selected
                                      ? (
                                          ("options" in input &&
                                            input.options &&
                                            input.options.find(
                                              (option) =>
                                                option.value?.toString() ===
                                                selected.toString(),
                                            )?.label) ||
                                          ""
                                        ).toString()
                                      : "value" in input && input.value
                                        ? (
                                            ("options" in input &&
                                              input.options &&
                                              input.options.find(
                                                (option) =>
                                                  option.value?.toString() ===
                                                  input.value?.toString(),
                                              )?.label) ||
                                            ""
                                          ).toString()
                                        : ""
                                  }
                                  placeholder={`${input.label}`}
                                  // Combobox field need to have a different name from the real field
                                  name={`_${input.name}`}
                                  ref={field.ref}
                                  onChange={(event) =>
                                    setQuery(event.target.value)
                                  }
                                />
                                <ComboboxButton
                                  className={styles.formComboboxButton}
                                >
                                  <Icon
                                    svg={ChevronDown}
                                    size={"18px"}
                                    className={styles.chevron}
                                  />
                                </ComboboxButton>
                              </div>
                              <ComboboxOptions
                                className={styles.formComboboxOptions}
                              >
                                {({ option: option }) => {
                                  return (
                                    <ComboboxOption
                                      key={option.value}
                                      value={option}
                                      className={styles.formComboboxOption({
                                        isFirst: true,
                                      })}
                                    >
                                      {option.label}
                                      <div
                                        style={{
                                          position: "relative",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <DrawnBorder
                                          className={styles.border}
                                        />
                                      </div>
                                    </ComboboxOption>
                                  );
                                }}
                              </ComboboxOptions>
                            </Combobox>
                          </FormInputFieldWrapper>
                        </div>
                      );

                      const [hideTooltip, setHideTooltip] = useState(false);
                      const [inputFocused, setInputFocused] = useState(false);

                      const PasswordTooltip = ({
                        open = false,
                      }: {
                        open: boolean;
                      }) => {
                        const password = watch(input.name) ?? "";
                        const validUpper =
                          password &&
                          countCharacters(password, defaultUpperCharacters) >=
                            1;
                        const validLower =
                          password &&
                          countCharacters(password, defaultLowerCharacters) >=
                            1;
                        const validDigit =
                          password &&
                          countCharacters(password, defaultDigitCharacters) >=
                            1;
                        const validSpecial =
                          password &&
                          countCharacters(password, defaultSpecialCharacters) >=
                            1;
                        const validLength = password && password.length >= 8;

                        return (
                          <div className={styles.tooltip}>
                            <Icon
                              svg={InfoCircle}
                              size="18px"
                              onClick={() => setHideTooltip(!hideTooltip)}
                            />

                            <WrapperDrawnBorder
                              className={styles.tooltipContainer({
                                visible: open && inputFocused,
                              })}
                            >
                              <span className={styles.tooltipItem}>
                                <Icon
                                  svg={validLength ? Check : X}
                                  size="12px"
                                />
                                <Paragraph size="Xs">
                                  Must be over 8 characters
                                </Paragraph>
                              </span>
                              <span className={styles.tooltipItem}>
                                <Icon
                                  svg={validDigit ? Check : X}
                                  size="12px"
                                />
                                <Paragraph size="Xs">
                                  Must contain 1 number
                                </Paragraph>
                              </span>
                              <span className={styles.tooltipItem}>
                                <Icon
                                  svg={validSpecial ? Check : X}
                                  size="12px"
                                />
                                <Paragraph size="Xs">
                                  Must contain 1 special character
                                </Paragraph>
                              </span>
                              <span className={styles.tooltipItem}>
                                <Icon
                                  svg={validUpper ? Check : X}
                                  size="12px"
                                />
                                <Paragraph size="Xs">
                                  Must contain 1 uppercase letter
                                </Paragraph>
                              </span>
                              <span className={styles.tooltipItem}>
                                <Icon
                                  svg={validLower ? Check : X}
                                  size="12px"
                                />
                                <Paragraph size="Xs">
                                  Must contain 1 lowercase letter
                                </Paragraph>
                              </span>
                            </WrapperDrawnBorder>
                          </div>
                        );
                      };

                      return "type" in input && input.type === "hidden" ? (
                        <input
                          key={index}
                          className={styles.input}
                          {...register(input.name)}
                          placeholder={input.label}
                          type={"type" in input ? input.type : "text"}
                          onBlur={() => setInputFocused(false)}
                          onFocus={() => setInputFocused(true)}
                          defaultValue={input?.value}
                        />
                      ) : (
                        <div className={styles.formInputs} key={index}>
                          {"type" in input && input.type === "select" ? (
                            <div>
                              {"parent" in input && input.parent
                                ? getValues(input.parent.name) ===
                                  input.parent.value
                                  ? ComboboxComponent
                                  : null
                                : ComboboxComponent}
                            </div>
                          ) : (
                            <div style={{ position: "relative" }}>
                              <FormInputFieldWrapper
                                theme={
                                  errors[input.name] || Boolean(responseError)
                                    ? "negative"
                                    : "positive"
                                }
                                selected={
                                  errors[input.name] ||
                                  Boolean(responseError) ||
                                  Boolean(watch(input.name))
                                    ? true
                                    : false
                                }
                              >
                                <div className={styles.inputWrapper}>
                                  <input
                                    className={styles.input}
                                    {...register(input.name)}
                                    placeholder={input.label}
                                    type={"type" in input ? input.type : "text"}
                                    onBlur={() => setInputFocused(false)}
                                    onFocus={() => setInputFocused(true)}
                                    defaultValue={input?.value}
                                    autoFocus={input?.autoFocus}
                                  />
                                </div>
                              </FormInputFieldWrapper>
                              {(form.schema[step].properties as any)[
                                input.name
                              ] &&
                                (form.schema[step].properties as any)[
                                  input.name
                                ].format === "strong-password" && (
                                  <PasswordTooltip
                                    // OPEN AND FOCUSED
                                    open={Boolean(watch(input.name))}
                                  />
                                )}
                            </div>
                          )}
                          {/* <div>{errors[input.name]?.message as string}</div> */}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {turnstile && (
                <div style={{ width: "100%" }}>
                  <CardTurnstile
                    icon={turnstileChallengeState?.icon}
                    theme={
                      (turnstileChallengeState?.theme as
                        | "primary"
                        | "positive"
                        | "negative") || "primary"
                    }
                  >
                    <Paragraph size="Sm">
                      {turnstileChallengeState?.label}
                    </Paragraph>
                  </CardTurnstile>
                  <Turnstile
                    ref={refTurnstile}
                    {...turnstile}
                    onError={() => setTurnstileVerification("error")}
                    onExpire={() => setTurnstileVerification("expired")}
                    onSuccess={(token) => {
                      setTurnstileVerification("solved");
                    }}
                  />
                </div>
              )}

              <div className={styles.buttons}>
                {form.inputs.length > 1 && (
                  <Button
                    width="full"
                    size="sm"
                    type="button"
                    disabled={
                      isSubmitting ||
                      (turnstile && turnstileVerification !== "solved") ||
                      step === 0
                    }
                    isDisabled={
                      isSubmitting ||
                      (turnstile && turnstileVerification !== "solved") ||
                      step === 0
                    }
                    onClick={() => setStep(step - 1)}
                    icon={ArrowLeft}
                    layout="left"
                  >
                    <Paragraph>Back</Paragraph>
                  </Button>
                )}
                <Button
                  width="full"
                  size="sm"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    (turnstile && turnstileVerification !== "solved")
                  }
                  isDisabled={
                    isSubmitting ||
                    (turnstile && turnstileVerification !== "solved")
                  }
                  icon={isStepLast ? Check : ArrowRight}
                  layout="right"
                  // theme={isStepLast ? "colour" : "dark"}
                  theme="dark"
                >
                  <Paragraph>
                    {isStepLast ? form.submit.label : "Continue"}
                  </Paragraph>
                </Button>
              </div>
              {"disclaimer" in form && form.disclaimer && isStepLast && (
                <Paragraph
                  className={styles.disclaimer}
                  size="Sm"
                  dangerouslySetInnerHTML={{ __html: form.disclaimer.text }}
                />
              )}
              {"footer" in form && form.footer && (
                <Paragraph size="Sm">
                  {form.footer.text}
                  {form.footer.link && (
                    <>
                      <span> </span>
                      <Link to={form.footer.link.to} className={styles.link}>
                        {form.footer.link.text}
                      </Link>
                    </>
                  )}
                </Paragraph>
              )}
            </div>
            {responseError && (
              <FormConfirmation error={{ message: responseError }} />
            )}
            {responseSuccess && (
              <FormConfirmation success={{ message: responseSuccess }} />
            )}
            {responseWarning && (
              // <div>
              <ModalForm
                alert={{
                  text: responseWarning.text,
                  heading: responseWarning.title,
                  theme: "negative",
                }}
                asModal={false}
                hasSound={false}
                open={true}
                setIsModalOpen={setIsModalOpen}
                buttons={responseWarning.actions.map((action, index) => ({
                  children: action.label,
                  key: index,
                  icon: index === 0 ? Plus : ArrowRight,
                  layout: "right",
                  theme: index === 0 ? "light" : "dark",
                  type: "submit",
                  name: action.name,
                  value: action.value,
                }))}
              />

              //   {/* <div>{responseWarning.title}</div>
              //   <div>{responseWarning.text}</div>
              //   {responseWarning.actions.map((action, index) => {
              //     return (
              //       <button
              //         key={index}
              //         type="submit"
              //         name={action.name}
              //         value={action.value}
              //       >
              //         {action.label}
              //       </button>
              //     );
              //   })} */}
              // {/* </div> */}
            )}
          </FormRemix>
          <div className={styles.steps}>
            {form.inputs.length > 1 && (
              <>
                {form.inputs.map((_, index) => {
                  return (
                    <ButtonIcon
                      hasBackground
                      key={index}
                      disabled={step < index}
                      // style={{ pointerEvents: "none" }}
                      onClick={() => setStep(index)}
                    >
                      <Paragraph size="Sm">{index + 1}</Paragraph>
                    </ButtonIcon>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </WrapperFormRoot>
    </section>
  );
};

export async function formSubmit(fields: any) {
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  await delay(2000);

  return {
    ok: true,
  };
}
